import { type JSX, useRef, useState } from 'react';

import { EloIcon, EloTooltip, type EloIconType, Menu } from '@plurall/elo-v3';

import { useAuthentication } from '@/hooks/queries/authentication/useAuthentication';
import { ENV } from '@/lib/constants/environment';
import { Partner } from '@/schemas/auth-user';

import { HelpMenuCardFactory, HelpMenuContentFactory, HelpMenuItemFactory } from './HelpMenu.factory';
import { AppNavIconButton } from '../AppNavIconButton/AppNavIconButton';
import { createMenuContentKeyHandler, createTriggerKeyHandler } from '../shared/menuKeyboardNav';

type HelpMenuTriggerProps = {
  isOpen: boolean;
  triggerRef: React.Ref<HTMLButtonElement>;
  onKeyDown: (e: React.KeyboardEvent) => void;
};

function getExternalUrl(path: string): string {
  return `${ENV.ANGULAR_PLATFORM_URL}/${path}`;
}

function HelpMenuTrigger({ isOpen, triggerRef, onKeyDown }: HelpMenuTriggerProps): JSX.Element {
  return (
    <EloTooltip
      content='Ajuda'
      position='bottom'
      openDelay={300}
      disabled={isOpen}
      size='small'
      closeOnPointerDown={false}
      closeOnClick={false}>
      <Menu.Trigger asChild>
        <AppNavIconButton
          ref={triggerRef}
          aria-label='Ajuda'
          icon={'question-circle'}
          iconHover={'question-circle-fill'}
          isActive={isOpen}
          onKeyDown={onKeyDown}
        />
      </Menu.Trigger>
    </EloTooltip>
  );
}

export function HelpMenu(): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const [isItemHovered, setIsItemHovered] = useState(false);
  const [isItemFocused, setIsItemFocused] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const openedFromKeyboardRef = useRef(false);
  const { data: user } = useAuthentication();

  const triggerTabHandler = createTriggerKeyHandler({ isOpen, contentRef });
  const handleTriggerKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      openedFromKeyboardRef.current = true;
      e.preventDefault();
      e.stopPropagation();
      setIsOpen((prev) => !prev);
      return;
    }
    triggerTabHandler(e);
  };
  const handleContentKeyDown = createMenuContentKeyHandler({ triggerRef, closeMenu: () => setIsOpen(false) });

  const isPartnerSesiB2g = user?.parceiroId === Partner.SESI_B2G;
  if (isPartnerSesiB2g) return <></>;

  const handleContactSelect = (): void => {
    setIsOpen(false);
    window.location.href = getExternalUrl('usuario/contato');
  };

  const contactIcon = (isItemHovered || isItemFocused ? 'headset-fill' : 'headset') as EloIconType;

  return (
    <Menu.Root
      open={isOpen}
      onOpenChange={({ open }) => {
        const nextOpen = open ?? false;
        setIsOpen(nextOpen);

        if (!nextOpen) {
          openedFromKeyboardRef.current = false;
        }
      }}
      positioning={{ placement: 'bottom-start', gutter: 16 }}
      size='md'
      variant='subtle'>
      <HelpMenuTrigger
        isOpen={isOpen}
        triggerRef={triggerRef}
        onKeyDown={handleTriggerKeyDown}
      />
      <Menu.Positioner>
        <HelpMenuContentFactory
          ref={contentRef}
          minW='220px'
          p={0}
          overflow='hidden'
          onOpenAutoFocus={(event: { preventDefault: () => void }) => {
            if (openedFromKeyboardRef.current) {
              event.preventDefault();
              triggerRef.current?.focus();
            }
          }}
          onKeyDown={handleContentKeyDown}>
          <HelpMenuCardFactory p='2'>
            <HelpMenuItemFactory
              value='contact-logged'
              tabIndex={0}
              onSelect={handleContactSelect}
              onPointerEnter={() => setIsItemHovered(true)}
              onPointerLeave={() => setIsItemHovered(false)}
              onFocus={() => setIsItemFocused(true)}
              onBlur={() => setIsItemFocused(false)}>
              <EloIcon
                icon={contactIcon}
                color='var(--elo-colors-foreground-brand)'
                flexShrink={0}
              />
              <span>Fale com a gente</span>
            </HelpMenuItemFactory>
          </HelpMenuCardFactory>
        </HelpMenuContentFactory>
      </Menu.Positioner>
    </Menu.Root>
  );
}
